import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

interface StatePoint {
  name: string;
  dryBulbTemp: number;
  relativeHumidity?: number;
  humidityRatio?: number;
  enthalpy?: number;
  displayName?: string;
}

interface StatePointsInfoProps {
  points: StatePoint[];
  title?: string;
}

const StatePointsInfo: React.FC<StatePointsInfoProps> = ({ points, title = '상태점 정보' }) => {
  // 냉방과 난방 상태점 분리
  // 난방 상태점: o2, r2, m1, l2, l3
  const heatingPoints = points.filter(p => {
    const name = p.name.toLowerCase();
    return name === 'o2' || name === 'r2' || name === 'm1' || name === 'l2' || name === 'l3';
  });

  // 냉방 상태점: 난방 상태점이 아닌 모든 상태점
  const coolingPoints = points.filter(p => {
    const name = p.name.toLowerCase();
    return !(name === 'o2' || name === 'r2' || name === 'm1' || name === 'l2' || name === 'l3');
  });

  // 상태점 이름 매핑 (습공기선도의 라벨과 동일)
  const getDisplayName = (point: StatePoint): string => {
    const pointName = (point.name || '').toLowerCase();

    // 습공기선도의 getFormattedLabelAndSymbol과 동일한 로직 사용
    if (pointName === 'r1') {
      return 'r1(환기 온도상승 후)';
    } else if (pointName.includes('ra') || (point.name.includes('환기') && point.name.includes('RA'))) {
      return 'ra(환기)';
    } else if (pointName === 'r2') {
      return 'r2(난방 환기)';
    } else if (pointName.includes('oa') || (point.name.includes('외기') && point.name.includes('OA'))) {
      return 'oa(외기)';
    } else if (pointName === 'o2') {
      return 'o2(난방 외기)';
    } else if (pointName.includes('ma') || (point.name.includes('혼합') && point.name.includes('MA'))) {
      return 'ma(혼합)';
    } else if (pointName === 'm1') {
      return 'm1(난방 혼합)';
    } else if (pointName.includes('l1') || (point.name.includes('냉각후') && point.name.includes('L1'))) {
      return 'l1(냉각후)';
    } else if (pointName === 'l2') {
      return 'l2(난방 급기)';
    } else if (pointName.includes('la') && !pointName.includes('sa')) {
      return 'la(Leave Air)';
    } else if (pointName === 'l3') {
      return 'l3(난방 가열후)';
    } else if (pointName.includes('sa') || (point.name.includes('급기') && point.name.includes('SA'))) {
      return 'sa(급기)';
    } else if (pointName.includes('adp')) {
      return 'adp';
    }

    return point.name.toLowerCase() || '-';
  };

  const renderPointRow = (point: StatePoint) => {
    const displayName = getDisplayName(point);

    return (
      <TableRow key={point.name}>
        <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.875rem', fontWeight: 'bold' }}>
          {displayName}
        </TableCell>
        <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.875rem', textAlign: 'right' }}>
          {point.dryBulbTemp.toFixed(1)}°C
        </TableCell>
        <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.875rem', textAlign: 'right' }}>
          {point.relativeHumidity !== undefined
            ? `${point.relativeHumidity.toFixed(1)}%`
            : '-'}
        </TableCell>
        <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.875rem', textAlign: 'right' }}>
          {point.enthalpy !== undefined
            ? `${point.enthalpy.toFixed(2)}`
            : '-'}
        </TableCell>
        <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.875rem', textAlign: 'right' }}>
          {point.humidityRatio !== undefined
            ? `${point.humidityRatio.toFixed(4)}`
            : '-'}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ width: '100%', p: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        {title}
      </Typography>

      {/* 냉방 상태점 */}
      {coolingPoints.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#2196F3', mb: 0.5, display: 'block' }}>
            ■ 냉방 상태점
          </Typography>
          <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>
                  상태점
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'right' }}>
                  건구온도(°C)
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'right' }}>
                  상대습도(%)
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'right' }}>
                  엔탈피(kJ/kg)
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'right' }}>
                  절대습도(kg/kg)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coolingPoints.map(renderPointRow)}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* 난방 상태점 */}
      {heatingPoints.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#F44336', mb: 0.5, display: 'block' }}>
            ■ 난방 상태점
          </Typography>
          <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>
                  상태점
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'right' }}>
                  건구온도(°C)
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'right' }}>
                  상대습도(%)
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'right' }}>
                  엔탈피(kJ/kg)
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'right' }}>
                  절대습도(kg/kg)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {heatingPoints.map(renderPointRow)}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default StatePointsInfo;
